import { FormEvent, ReactNode, useEffect, useState, useSyncExternalStore } from "react";
import config from "@/site.config.json";
import { BrandMark } from "@/src/components/BrandMark";

const KEY = "responsible-architectures-student-access";
const EVENT = "responsible-architectures-access-change";

function subscribe(callback: () => void) {
  window.addEventListener(EVENT, callback);
  return () => window.removeEventListener(EVENT, callback);
}

async function sha256(value: string) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function StudentGate({ children }: { children: ReactNode }) {
  const allowed = useSyncExternalStore(subscribe, () => sessionStorage.getItem(KEY) === "granted", () => false);
  const [phrase, setPhrase] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!allowed) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [allowed]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const candidate = await sha256(phrase.trim());
    if (candidate === config.gate_hash) {
      sessionStorage.setItem(KEY, "granted");
      window.dispatchEvent(new Event(EVENT));
      setMessage("");
    } else {
      setMessage("That phrase was not recognised. Check the phrase supplied by your instructor.");
    }
  }

  if (allowed) return <>{children}</>;

  return (
    <main id="main-content" className="gate-page">
      <div className="gate-card">
        <BrandMark className="gate-brand-mark" />
        <p className="eyebrow">Enrolled students</p>
        <h1>Enter the course area</h1>
        <p>Use the access phrase provided by your instructor. Your access lasts for this browser session.</p>
        <form onSubmit={submit}>
          <label htmlFor="access-phrase">Access phrase</label>
          <input id="access-phrase" type="password" autoComplete="current-password" value={phrase} onChange={(event) => setPhrase(event.target.value)} aria-describedby={message ? "gate-error" : "gate-help"} />
          <small id="gate-help">The phrase is case-sensitive.</small>
          {message && <p className="form-error" id="gate-error" role="alert">{message}</p>}
          <button className="button primary" type="submit">Continue to the course</button>
        </form>
        <p className="gate-note">Need access? Contact your course team. This prompt is a cohort signpost, not an account login.</p>
      </div>
    </main>
  );
}
